import {
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Typography,
  Upload,
} from "antd";
import { UploadFile } from "antd/lib/upload/interface";
import ptBR from "antd/es/date-picker/locale/pt_BR";
import moment from "moment";
import "moment/locale/pt-br";
import { InfoCircleOutlined } from "@ant-design/icons";
import { EQuestionType } from "../enums/EQuestionType";
import { SectionTitle } from "./SectionTitle";
import { IPostCategorySection } from "../utils/types/IPostCategory";
import { uploadFileToS3 } from "../utils/services/aws";
import { Headers } from "../utils/types/Headers";
import { notificationError } from "../utils/common/notificationError";
import { FILE_TYPES, getMimeType } from "../constants/files";
import { UploadProps } from "antd/lib/upload";

type FileUploadProps = UploadProps & {
  fileList?: UploadFile[];
};

const FileUpload = ({ fileList, maxCount, ...rest }: FileUploadProps) => {
  const currentCount = fileList?.length ?? 0;
  const reachedLimit = maxCount != null && currentCount >= maxCount;

  return (
    <Upload {...rest} maxCount={maxCount} fileList={fileList}>
      {!reachedLimit && <div>+ Upload</div>}
    </Upload>
  );
};

type Props = {
  sections: IPostCategorySection[];
  headers: Headers;
};

export const AdditionalInfoSection = ({ sections, headers }: Props) => {
  const visibleSections = sections.filter((section) => !section.hidden);

  if (!visibleSections.length) return null;

  return (
    <>
      <SectionTitle
        title="Informações adicionais"
        tooltip="Preencha as informações adicionais solicitadas pela categoria"
      />
      {visibleSections.map((section, sectionIndex, arr) => (
        <div key={section.id} style={{ marginBottom: 16 }}>
          <Typography.Title level={5} style={{ marginBottom: 16 }}>
            {section.name}
          </Typography.Title>
          {section.questions
            .filter((q) => !q.hidden)
            .map((question) => {
              const fieldName = [
                "additionalInfo",
                `section_${section.id}`,
                `${question.question}:${question.answerType}:id_${question.id}`,
              ];

              const rules = question.isMandatory
                ? [{ required: true, message: "Campo obrigatório" }]
                : [];

              const renderQuestion = () => {
                switch (question.answerType) {
                case EQuestionType.OPEN_FIELD:
                  return (
                    <Form.Item
                      key={question.id}
                      label={question.question}
                      name={fieldName}
                      rules={rules}
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder="Digite sua resposta"
                      />
                    </Form.Item>
                  );
                case EQuestionType.BOOLEAN:
                  return (
                    <Form.Item
                      key={question.id}
                      label={question.question}
                      name={fieldName}
                      rules={rules}
                    >
                      <Radio.Group>
                        <Radio value="Sim">Sim</Radio>
                        <Radio value="Não">Não</Radio>
                      </Radio.Group>
                    </Form.Item>
                  );
                case EQuestionType.NUMBER:
                  return (
                    <Form.Item
                      key={question.id}
                      label={question.question}
                      name={fieldName}
                      rules={rules}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Digite um número"
                      />
                    </Form.Item>
                  );
                case EQuestionType.DROPDOWN: {
                  const rawOptions = question.extraProps?.options || [];
                  const options = (rawOptions as any[]).map((opt: any) =>
                    typeof opt === "string" ? opt : opt.value,
                  );
                  const isMultiple =
                    question.extraProps?.multipleAnswer ?? false;
                  return (
                    <Form.Item
                      key={question.id}
                      label={question.question}
                      name={fieldName}
                      rules={rules}
                    >
                      <Select
                        mode={isMultiple ? "multiple" : undefined}
                        placeholder="Selecione uma opção"
                        options={options.map((opt: string) => ({
                          label: opt,
                          value: opt,
                        }))}
                      />
                    </Form.Item>
                  );
                }
                case EQuestionType.DATE_FIELD: {
                  const allowFutureDates =
                    question.allowFutureDates === true;
                  return (
                    <Form.Item
                      key={question.id}
                      label={question.question}
                      name={fieldName}
                      rules={rules}
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        placeholder="Selecione uma data"
                        locale={ptBR}
                        disabledDate={
                          allowFutureDates
                            ? undefined
                            : (current) =>
                                current &&
                                current > moment().endOf("day")
                        }
                      />
                    </Form.Item>
                  );
                }
                case EQuestionType.GROUPED_LIST: {
                  const categories =
                    (question.extraProps?.categories as {
                      label: string;
                      hidden?: boolean;
                      subcategories: { value: string; hidden?: boolean }[];
                    }[]) || [];
                  const visibleCategories = categories.filter((c) => !c.hidden);
                  return (
                    <Form.Item
                      key={question.id}
                      label={question.question}
                      name={fieldName}
                      rules={rules}
                    >
                      <Select
                        placeholder="Selecione uma opção"
                        mode={
                          question.extraProps?.multipleAnswer
                            ? "multiple"
                            : undefined
                        }
                      >
                        {visibleCategories.map((cat) => (
                          <Select.OptGroup label={cat.label} key={cat.label}>
                            {cat.subcategories
                              .filter((sub) => !sub.hidden)
                              .map((sub) => (
                                <Select.Option
                                  key={`${cat.label}_${sub.value}`}
                                  value={`${cat.label}\0${sub.value}`}
                                >
                                  {sub.value}
                                </Select.Option>
                              ))}
                          </Select.OptGroup>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }
                case EQuestionType.FILE: {
                  const fileConfig = question.extraProps?.fileConfig || {
                    maxFiles: 1,
                    maxFileSize: 2,
                    type: "pdf",
                  };
                  const fileTypeConfig =
                    FILE_TYPES[fileConfig.type as keyof typeof FILE_TYPES];
                  const extensions = fileTypeConfig?.extensions ?? [];
                  const allowedMimes = extensions
                    .map((ext) => getMimeType(ext))
                    .filter(Boolean) as string[];
                  const accept = extensions.join(",");
                  const isAnyType = (fileConfig.type as string) === "ANY";
                  const acceptedExts = extensions.map((ext) => ext.trim());
                  const formatsText =
                    acceptedExts.length > 1
                      ? `${acceptedExts.slice(0, -1).join(", ")} ou ${
                          acceptedExts[acceptedExts.length - 1]
                        }`
                      : acceptedExts[0] ?? "";
                  const filesWord =
                    fileConfig.maxFiles === 1 ? "arquivo" : "arquivos";
                  const limitText = question.isMandatory
                    ? fileConfig.maxFiles === 1
                      ? `Você deve enviar 1 ${filesWord}`
                      : `Você deve enviar ao menos 1 e até ${fileConfig.maxFiles} ${filesWord}`
                    : `Você pode enviar até ${fileConfig.maxFiles} ${filesWord}`;
                  const hintText = `${limitText} (máx. ${fileConfig.maxFileSize}MB) ${
                    isAnyType || !formatsText
                      ? "em qualquer formato"
                      : `nos formatos ${formatsText}`
                  }`;
                  const fileRules = question.isMandatory
                    ? [
                        {
                          required: true,
                          validator: (_: unknown, value: UploadFile[]) => {
                            const hasValidFile = (value ?? []).some(
                              (f: any) =>
                                f.status === "done" &&
                                (f.url || f.response?.url),
                            );
                            return hasValidFile
                              ? Promise.resolve()
                              : Promise.reject(new Error("Campo obrigatório"));
                          },
                        },
                      ]
                    : [];
                  return (
                    <div key={question.id}>
                      <Form.Item
                        key={`${question.id}-item`}
                        label={question.question}
                        name={fieldName}
                        rules={fileRules}
                      valuePropName="fileList"
                      getValueFromEvent={(e) => e?.fileList ?? []}
                    >
                      <FileUpload
                        maxCount={fileConfig.maxFiles}
                        accept={accept}
                        listType="picture-card"
                        beforeUpload={(file) => {
                          const maxSize = fileConfig.maxFileSize * 1024 * 1024;
                          if (file.size > maxSize) {
                            notificationError(
                              `Arquivo deve ter no máximo ${fileConfig.maxFileSize}MB`,
                            );
                            return Upload.LIST_IGNORE;
                          }
                          if (
                            allowedMimes.length > 0 &&
                            !allowedMimes.includes(file.type)
                          ) {
                            notificationError(
                              accept
                                ? `Tipo de arquivo não permitido. Tipos aceitos: ${accept}`
                                : `Tipo de arquivo não permitido: ${file.type}`,
                            );
                            return Upload.LIST_IGNORE;
                          }
                          return true;
                        }}
                        customRequest={async (options) => {
                          const file = options.file as UploadFile;
                          const fileName = `${Date.now()}_$colab_file%_${encodeURIComponent(
                            file.name,
                          )}`;
                          const bucket = process.env.REACT_APP_S3_FILES_BUCKET!;
                          try {
                            const uploaded = await uploadFileToS3({
                              file,
                              bucket,
                              fileName,
                              fileType: file.type!,
                              headers,
                            });
                            if (!uploaded?.url) {
                              options.onError?.(
                                new Error("Upload failed: missing URL"),
                              );
                              return;
                            }
                            options.onSuccess?.(
                              { url: uploaded.url },
                              uploaded as any,
                            );
                          } catch (err) {
                            options.onError?.(err as Error);
                          }
                        }}
                      />
                    </Form.Item>
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "flex-start",
                          marginTop: -16,
                        }}
                      >
                        <InfoCircleOutlined
                          style={{
                            fontSize: 12,
                            color: "rgba(0,0,0,0.45)",
                            marginTop: 3,
                          }}
                        />
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          {hintText}
                        </Typography.Text>
                      </div>
                    </div>
                  );
                }
                  default:
                    return null;
                }
              };

              return (
                <div key={question.id} style={{ marginBottom: 16 }}>
                  {renderQuestion()}
                </div>
              );
            })}
          {sectionIndex !== arr.length - 1 && <Divider />}
        </div>
      ))}
    </>
  );
};
